/** @fileoverview Finds connected systems and the app components bound to them: data sources, exports and links. */

import { isRecord } from './utils';

import type {
  FabrikAppDefinition,
  FabrikButtonTarget,
  FabrikComponent,
  FabrikListComponent,
  FabrikLinkUrlTemplate,
  FabrikSystemDataSource,
  FabrikPdfDocument,
} from '../types/apps';

export type AppSystemDataSourceBinding = {
  entity: string;
  fieldsBound: number;
};

export type AppSystemBinding = {
  systemId: string;
  componentCount: number;
  dataSources: AppSystemDataSourceBinding[];
  usesSharePointExport: boolean;
};

function collectFields(value: unknown, dataSourceId: string, fields: Set<string>) {
  if (!value || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectFields(item, dataSourceId, fields));
    return;
  }

  const reference = value as {
    type?: string;
    scope?: string;
    dataSourceId?: string;
    field?: string;
  };
  if (
    reference.type === 'ref' &&
    reference.scope === 'dataSource' &&
    reference.dataSourceId === dataSourceId &&
    reference.field
  ) {
    fields.add(reference.field);
  }
}

function countBoundFields(component: FabrikListComponent, source: FabrikSystemDataSource) {
  const fields = new Set<string>();
  for (const column of component.columns) {
    if (column.kind === 'computed') {
      Object.values(column.inputs).forEach((input) => collectFields(input, source.id, fields));
    } else if (column.kind === 'person') {
      collectFields(column.primary, source.id, fields);
      collectFields(column.secondary, source.id, fields);
    } else if (column.kind === 'status') {
      collectFields(column.value, source.id, fields);
    } else {
      collectFields(column.value, source.id, fields);
    }
  }
  for (const filter of component.filters ?? []) fields.add(filter.field);
  for (const sort of component.sortChoices ?? [])
    sort.orderBy.forEach(({ field }) => fields.add(field));
  for (const join of component.joins ?? []) {
    if (join.dataSourceId === source.id) fields.add(join.field);
    collectFields(join.on, source.id, fields);
  }
  for (const filter of source.type === 'odata' ? source.rowFilters ?? [] : []) {
    fields.add(filter.field);
    collectFields(filter.value, source.id, fields);
  }

  return fields.size || source.values?.length || 0;
}

/** Lists the systems whose settings the blanks of a link address read. */
const readUrlSystemIds = (url: string | FabrikLinkUrlTemplate | undefined): string[] =>
  url && typeof url !== 'string'
    ? Object.values(url.values).flatMap((value) =>
        isRecord(value) && value.scope === 'system' && typeof value.systemId === 'string'
          ? [value.systemId]
          : []
      )
    : [];

/** Lists the systems whose settings the blanks of a link target read. */
const readLinkSystemIds = (target: FabrikButtonTarget | undefined): string[] =>
  target?.type === 'link' ? readUrlSystemIds(target.url) : [];

/** Lists the systems whose settings the link blanks of a PDF document read. */
const readDocumentSystemIds = (document: FabrikPdfDocument): string[] =>
  document.blocks.flatMap((block) => {
    if (block.kind === 'fields') return block.items.flatMap(({ link }) => readUrlSystemIds(link));
    return block.kind === 'table' ? readUrlSystemIds(block.link?.url) : [];
  });

/** Returns system IDs used by OData lists, SharePoint export actions, link blanks or PDF link blanks. */
export function getAppSystemBindings(definition?: FabrikAppDefinition): AppSystemBinding[] {
  const bindings = new Map<
    string,
    {
      componentIds: Set<string>;
      dataSources: Map<string, AppSystemDataSourceBinding>;
      usesSharePointExport: boolean;
    }
  >();
  const actionComponents = new Map<string, Set<string>>();

  const getBinding = (systemId: string) => {
    let binding = bindings.get(systemId);
    if (!binding) {
      binding = {
        componentIds: new Set(),
        dataSources: new Map(),
        usesSharePointExport: false,
      };
      bindings.set(systemId, binding);
    }
    return binding;
  };

  const visit = (screenId: string, component: FabrikComponent) => {
    if (component.kind === 'grid') {
      component.items.forEach(({ component: child }) => visit(screenId, child));
      return;
    }
    if (component.kind === 'card') {
      visit(screenId, component.content);
      return;
    }
    const child = component;
    const componentId = `${screenId}:${child.id}`;
    const actionTargets =
      child.kind === 'button'
        ? [child.target]
        : child.kind === 'list'
        ? (child.headerActions ?? []).map(({ target }) => target)
        : child.kind === 'checklist'
        ? (child.itemActions ?? []).map(({ target }) => target)
        : [];
    for (const target of actionTargets) {
      if (target.type !== 'action') continue;
      const components = actionComponents.get(target.actionId) ?? new Set<string>();
      components.add(componentId);
      actionComponents.set(target.actionId, components);
    }

    const linkTarget = child.kind === 'button' || child.kind === 'list' ? child.target : undefined;
    for (const systemId of readLinkSystemIds(linkTarget)) {
      getBinding(systemId).componentIds.add(componentId);
    }

    if (child.kind === 'metric' && child.dataSource.type !== 'items') {
      const source = child.dataSource;
      const binding = getBinding(source.systemId);
      binding.componentIds.add(componentId);
      binding.dataSources.set(`${screenId}:${child.id}:${source.id}`, {
        entity: source.entity,
        fieldsBound: new Set([
          ...(source.values ?? []),
          ...(source.type === 'odata' ? source.rowFilters ?? [] : []).map(({ field }) => field),
        ]).size,
      });
    }
    if (child.kind === 'list') {
      for (const source of child.dataSource) {
        if (source.type === 'items') continue;
        const binding = getBinding(source.systemId);
        binding.componentIds.add(componentId);

        const sourceKey = `${screenId}:${child.id}:${source.id}`;
        if (!binding.dataSources.has(sourceKey)) {
          binding.dataSources.set(sourceKey, {
            entity: source.entity,
            fieldsBound: countBoundFields(child, source),
          });
        }
      }
    }
  };

  for (const screen of definition?.screens ?? []) {
    screen.components.forEach((component) => visit(screen.id, component));
  }

  for (const action of definition?.actions ?? []) {
    const componentIds = actionComponents.get(action.id);
    if (!componentIds) continue;
    for (const step of action.steps) {
      if (step.kind !== 'export' || step.to.type !== 'sharepoint') continue;
      const binding = getBinding(step.to.systemId);
      componentIds.forEach((componentId) => binding.componentIds.add(componentId));
      binding.usesSharePointExport = true;
    }
  }

  const documents = new Map(
    (definition?.documents ?? []).map((document) => [document.id, document])
  );
  for (const action of definition?.actions ?? []) {
    const componentIds = actionComponents.get(action.id);
    if (!componentIds) continue;
    for (const step of action.steps) {
      const document =
        step.kind === 'export' && step.format === 'pdf' ? documents.get(step.document) : undefined;
      if (!document) continue;
      for (const systemId of readDocumentSystemIds(document)) {
        const binding = getBinding(systemId);
        componentIds.forEach((componentId) => binding.componentIds.add(componentId));
      }
    }
  }

  return [...bindings].map(([systemId, binding]) => ({
    systemId,
    componentCount: binding.componentIds.size,
    dataSources: [...binding.dataSources.values()],
    usesSharePointExport: binding.usesSharePointExport,
  }));
}
